import { rememberReturnTo, takeReturnTo } from './api.js'

// Phones sign in with the whole page, not a popup.
//
// A phone's browser opens the popup as a tab and puts this one in the background, and the
// hand-back between the two is where it broke: Microsoft's answer reached a freshly loaded
// copy of the app, which had no reason to read it. The code sat in the address bar, the page
// showed the sign-in screen again, and there was no error, because the page that would have
// shown one had been replaced. So on a phone the button sends this page to Microsoft (or ICE)
// and back, and the page that loads finishes the sign-in.
//
// Desktops keep the popup. ICE's PLAN.md 1.5 promises existing users no difference there.

const PENDING_KEY = 'crew-dispatch-service:signinPending'
const ICE_ANSWER = '#ice-signin='

/** Whether this browser should sign in with the whole page: phones and tablets. */
export function signsInFullPage(nav = globalThis.navigator) {
  if (!nav) return false
  if (nav.userAgentData?.mobile) return true
  const ua = nav.userAgent || ''
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true
  // iPadOS asks for desktop sites and says it's a Mac. Only the touch points give it away.
  return /Macintosh/.test(ua) && nav.maxTouchPoints > 1
}

/**
 * Called just before this page goes to sign in. Notes where the person was, and that the
 * answer coming back is one this tab asked for: an address carrying a sign-in answer is
 * acted on only then, so a link can't feed the sign-in screen an answer of its own.
 */
export function leavingToSignIn(via) {
  rememberReturnTo()
  try {
    sessionStorage.setItem(PENDING_KEY, via)
  } catch {
    // Storage disabled. The sign-in still happens; this tab just won't finish it.
  }
}

/**
 * What the address holds after a full-page sign-in, read once as the app boots, before
 * anything routes. ICE's answer comes off the address straight away. Microsoft's stays for
 * MSAL to read, as it checks the answer against what it stored before leaving.
 *
 * A sign-in that worked through ICE already has its session, so the signed-in app renders
 * and the sign-in screen never does. The way back to where the person was is set here for
 * that reason.
 */
export function takeSigninReturn(loc = window.location, history = window.history) {
  let via = null
  try {
    via = sessionStorage.getItem(PENDING_KEY)
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    return null
  }
  // Read before anything below changes the address: window.location is live.
  const hash = loc.hash
  if (via === 'microsoft' && /[#&](code|error)=/.test(hash)) return { via }
  if (hash.startsWith(ICE_ANSWER)) {
    history.replaceState(null, '', loc.pathname + loc.search)
    if (via !== 'ice') return null
    let answer
    try {
      answer = JSON.parse(decodeURIComponent(hash.slice(ICE_ANSWER.length)))
    } catch {
      answer = { error: { code: 'SIGNIN_FAILED', message: 'Sign-in did not complete — please try again' } }
    }
    if (!answer?.error && !answer?.needsTenantChoice) {
      const back = takeReturnTo()
      if (back) history.replaceState(null, '', back)
    }
    return { via, answer }
  }
  return null
}

let returned = null
let finishing = null

/** main.jsx calls this once, before the first render. */
export function noteSigninReturn() {
  returned = takeSigninReturn()
}

/**
 * The sign-in this page load came back from, finished: the same answer the popup gives, or
 * null if it didn't come back from one. The same promise every time it's asked, so React's
 * development double-mount doesn't hand Microsoft's code in twice.
 */
export function finishSigninReturn({ ice, microsoft }) {
  if (!returned) return null
  finishing ??= returned.via === 'ice' ? ice(returned.answer) : microsoft()
  return finishing
}
